/*
 * Mobile sticky CTA.
 *
 * Not in the design reference; added per owner request. The bar slides up once
 * the inline product CTA has scrolled out of view and hides again when it comes
 * back. Phones only, gated by a media query in peanut.css.
 *
 * The bar ships with display:none and only gets --enabled here, so a page with
 * no JS or no IntersectionObserver renders exactly as the reference does.
 */
(function () {
  'use strict';

  if (!('IntersectionObserver' in window)) return;

  // Both the hero and the product section reference this file, because both
  // own a [data-peanut-video]. Same URL means one fetch, but two <script> tags
  // still execute twice, which would double every observer and every play or
  // pause call. Run once per page.
  if (window.peanutMediaInit) return;
  window.peanutMediaInit = true;

  var MOBILE = '(max-width: 767px)';

  document.querySelectorAll('[data-peanut-product]').forEach(function (section) {
    var bar = section.querySelector('[data-peanut-sticky-cta]');
    var anchor = section.querySelector('[data-peanut-cta-anchor]');

    if (!bar || !anchor) return;

    bar.classList.add('peanut-sticky-cta--enabled');

    /*
     * Reserve the bar's height at the foot of the page. The bar is fixed, so
     * without this it sits permanently on top of whatever the last 69px of
     * content happens to be, and on a short phone that was the gallery
     * thumbnails. Reserved once and always, not toggled with visibility, so
     * showing and hiding the bar never shifts the layout.
     */
    document.body.classList.add('peanut-has-sticky-cta');

    /*
     * The reservation is the bar's measured height, not a constant. It was a
     * hard coded 69px that went stale when the buttons grew to 50px, leaving
     * 6px of every page under the bar, and a longer label or a home indicator
     * would have grown it further. The bar is display: none on desktop, where
     * it measures 0, so only a real height is written.
     */
    if ('ResizeObserver' in window) {
      new ResizeObserver(function () {
        var height = bar.offsetHeight;
        if (height > 0) {
          document.documentElement.style.setProperty('--sticky-cta-h', height + 'px');
        }
      }).observe(bar);
    }

    /*
     * Two pages want opposite things from the same bar.
     *
     * The home page bar is a persistent offer: it belongs in the long middle of
     * the page, after the hero and before the closing CTA, so it shows whenever
     * neither of those is on screen.
     *
     * The product page bar used to be a fallback for a control you had already
     * met, and asked for 'passed': stay hidden until the inline CTA has gone
     * by. The reasoning was that showing it at the top buys nothing and costs
     * the bottom strip of the gallery. Reversed on 2026-10-11 at the owner's
     * decision: on a phone the inline button is a screen and a half down, so
     * until then there was nothing to buy from. No section asks for 'passed'
     * today. The mode is kept so one attribute on a bar brings it back.
     */
    var mode = bar.getAttribute('data-peanut-sticky-from');

    function show(visible) {
      // Only ever visible on phones. The media query hides the bar anyway.
      // aria-hidden is kept in step for assistive tech; the CSS visibility rule
      // is what takes the hidden button out of the tab order.
      var onMobile = window.matchMedia(MOBILE).matches;
      var reveal = visible && onMobile;

      bar.setAttribute('data-visible', reveal ? 'true' : 'false');
      bar.setAttribute('aria-hidden', reveal ? 'false' : 'true');
    }

    /*
     * Two things can make the bar redundant: its own inline CTA being on
     * screen, and the hero, which carries an identical CTA of its own. The bar
     * used to watch only the first, so at page load, with the closing CTA a
     * full page away, it appeared instantly and stacked a second Get the
     * bottle directly under the hero's. Now it waits for both to leave.
     */
    var anchorVisible = false;
    var anchorPassed = false;
    var heroVisible = false;
    var hero = document.querySelector('.peanut-hero');

    /*
     * Two more reasons to step aside, added 2026-10-10.
     *
     * Any other buy button on screen. The argument section's Get the bottle and
     * the bar's used to sit on screen together, the same offer twice. Every
     * solid or outline button that links to a product counts.
     *
     * A focused text field. The bar is fixed to the layout viewport, so with
     * the keyboard up on iOS it floated over the field being typed into, the
     * Roast list signup on the home page.
     */
    var otherCtas = Array.prototype.filter.call(
      document.querySelectorAll('a.peanut-btn[href*="/products/"]'),
      function (link) {
        return !bar.contains(link) && !anchor.contains(link) && !(hero && hero.contains(link));
      }
    );
    var visibleCtas = 0;
    var typing = false;

    function update() {
      var wanted = mode === 'passed' ? anchorPassed : !anchorVisible;
      show(wanted && !heroVisible && visibleCtas === 0 && !typing);
    }

    function isTextField(el) {
      if (!el || !el.matches) return false;
      if (el.matches('textarea, select')) return true;
      return el.matches('input') && !el.matches('[type="radio"], [type="checkbox"], [type="submit"], [type="button"], [type="hidden"]');
    }

    document.addEventListener('focusin', function (event) {
      if (!isTextField(event.target)) return;
      typing = true;
      update();
    });

    document.addEventListener('focusout', function (event) {
      if (!isTextField(event.target)) return;
      typing = false;
      update();
    });

    var ctaState = new Map();

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.target === anchor) {
            anchorVisible = entry.isIntersecting;
            // Above the viewport, not merely off it. Below and not yet reached
            // is the state the PDP bar has to sit out.
            anchorPassed = entry.boundingClientRect.bottom < 0;
          } else if (entry.target === hero) {
            heroVisible = entry.isIntersecting;
          } else {
            ctaState.set(entry.target, entry.isIntersecting);
          }
        });
        visibleCtas = 0;
        ctaState.forEach(function (on) {
          if (on) visibleCtas += 1;
        });
        update();
      },
      { rootMargin: '0px 0px -80px 0px', threshold: 0 }
    );

    observer.observe(anchor);
    otherCtas.forEach(function (link) {
      observer.observe(link);
    });
    if (hero) {
      heroVisible = true; // the hero is the fold; assume visible until told otherwise
      observer.observe(hero);
    }
    show(false);

    // Re-evaluate on rotate or resize across the breakpoint.
    window.matchMedia(MOBILE).addEventListener('change', function () {
      var rect = anchor.getBoundingClientRect();
      anchorVisible = rect.bottom >= 0 && rect.top <= window.innerHeight;
      anchorPassed = rect.bottom < 0;
      if (hero) {
        var hr = hero.getBoundingClientRect();
        heroVisible = hr.bottom >= 0 && hr.top <= window.innerHeight;
      }
      update();
    });
  });

  /*
   * Reduced motion: pause the product video and hold a single frame. This is an
   * accessibility requirement from the PRD, not a nice to have. CSS cannot pause
   * a <video>, so it is handled here.
   */
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

  /*
   * A video with no loop attribute is meant to run once and hold its last
   * frame. Three separate paths below resume playback (motion preference,
   * coming back on screen, and the tab being refocused), and any one of them
   * would restart a finished video from the beginning. Remember that it has
   * ended and refuse to start it again. A looping video never fires 'ended',
   * so this costs it nothing.
   */
  document.querySelectorAll('[data-peanut-video]').forEach(function (video) {
    video.addEventListener('ended', function () {
      video.dataset.peanutEnded = 'true';
    });
  });

  function resume(video) {
    if (video.dataset.peanutEnded === 'true') return;
    var attempt = video.play();
    if (attempt && typeof attempt.catch === 'function') {
      // Autoplay can still be refused. The poster carries the frame.
      attempt.catch(function () {});
    }
  }

  /*
   * Reduced motion gets the finished frame, not frame zero.
   *
   * Pausing on its own used to leave the hero on an empty studio backdrop,
   * because hero_video2.mp4 opens with the bottle out of shot and rises into
   * frame over about three seconds. The one group that never sees the animation
   * was the one group shown the frame before it starts. The poster covers this
   * only until the first frame decodes; after that the video element paints its
   * own frame and the poster is gone, so the seek is what actually holds it.
   *
   * Seeking needs metadata, which has usually not arrived when this first runs.
   * A browser that refuses the seek keeps frame zero, which is the behaviour
   * this replaces rather than a new failure.
   */
  function holdFinalFrame(video) {
    function seek() {
      if (!isFinite(video.duration) || video.duration <= 0) return;
      try {
        video.currentTime = Math.max(0, video.duration - 0.05);
      } catch (e) {}
    }

    if (video.readyState >= 1) seek();
    else video.addEventListener('loadedmetadata', seek, { once: true });
  }

  /*
   * The hero paints its last frame first (the poster) and the clip opens on an
   * empty set. Starting the clip after the visitor has already seen the
   * finished bottle rewinds the product out of frame: measured live, the bottle
   * showed from about 1.5s, vanished when playback began at 4.6s, and rose
   * again. So the intro only plays if it can start within INTRO_GRACE_MS of
   * first contentful paint, which is soon enough to read as an entrance.
   * Otherwise the video takes the reduced motion path: paused, marked ended,
   * held on its last frame, which is the poster, so nothing on screen changes.
   * Looping videos and anything outside the hero, the product spin, are left
   * alone.
   */
  var INTRO_GRACE_MS = 300;

  function introDeadline() {
    var fcp = null;
    if (window.performance && performance.getEntriesByName) {
      var entry = performance.getEntriesByName('first-contentful-paint')[0];
      if (entry) fcp = entry.startTime;
    }
    return (fcp === null ? performance.now() : fcp) + INTRO_GRACE_MS;
  }

  function abandonIntro(video) {
    video.removeAttribute('autoplay');
    video.pause();
    video.dataset.peanutEnded = 'true';
    holdFinalFrame(video);
    // Autoplay can already be queued inside the browser by the time the
    // attribute goes. If it fires anyway, put the frame back.
    video.addEventListener('playing', function () {
      video.pause();
      holdFinalFrame(video);
    }, { once: true });
  }

  function guardHeroIntro(video) {
    if (video.loop || !video.closest('.peanut-hero')) return;
    if (reduce.matches) return;
    if (!video.paused && video.currentTime > 0) return;

    var wait = introDeadline() - performance.now();
    if (wait <= 0) {
      abandonIntro(video);
      return;
    }

    var started = false;
    video.addEventListener('playing', function () { started = true; }, { once: true });
    window.setTimeout(function () {
      if (!started && video.currentTime === 0) abandonIntro(video);
    }, wait);
  }

  function applyMotionPreference() {
    document.querySelectorAll('[data-peanut-video]').forEach(function (video) {
      if (reduce.matches) {
        video.removeAttribute('autoplay');
        video.pause();
        holdFinalFrame(video);
      } else if (video.paused) {
        resume(video);
      }
    });
  }

  document.querySelectorAll('[data-peanut-video]').forEach(guardHeroIntro);
  applyMotionPreference();
  reduce.addEventListener('change', applyMotionPreference);

  /*
   * Battery: stop decoding the video while it is off screen or the tab is
   * backgrounded. Same budget rule the WebGL layer will follow.
   */
  var videoObserver = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      var video = entry.target;
      if (reduce.matches) return;

      if (entry.isIntersecting) {
        resume(video);
      } else {
        video.pause();
      }
    });
  });

  document.querySelectorAll('[data-peanut-video]').forEach(function (video) {
    videoObserver.observe(video);
  });

  document.addEventListener('visibilitychange', function () {
    document.querySelectorAll('[data-peanut-video]').forEach(function (video) {
      if (document.hidden) {
        video.pause();
      } else if (!reduce.matches) {
        resume(video);
      }
    });
  });
})();
